import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getCableConsumer, reconnectCable } from '@/lib/actionCable'
import { useToast } from '@/components/ui'
import { api, type Conversation, type Message } from '@/lib/api'
import { playNotificationSound } from '@/stores/chatSoundStore'
import { encodeId } from '@/lib/hashId'
import type { Subscription } from '@rails/actioncable'
import { usePlanFeature } from '@/hooks/usePlanFeature'

// Global state for singleton subscription
let globalSubscription: Subscription | null = null
const messageHandlers: Set<(data: ChatMessage) => void> = new Set()

// Reset chat subscription (call after login to reconnect with fresh token)
export function resetChatSubscription() {
  if (globalSubscription) {
    globalSubscription.unsubscribe()
    globalSubscription = null
  }
  // Reconnect cable with new token
  reconnectCable()
}

/**
 * Mensagem como ela vive no cache de `['conversation', id]`.
 *
 * O `id` admite string porque o envio otimista insere um `temp-<timestamp>` antes de o
 * servidor responder; o `Message` que vem da API e sempre numerico.
 */
type CachedMessage = Omit<Message, 'id'> & { id: number | string }

interface ConversationCache {
  success: boolean
  conversation: Conversation
  messages: CachedMessage[]
}

interface ChatMessage {
  type: string
  message?: Message
  conversation_id?: number
  sender_name?: string
  preview?: string
  user_id?: number
  user_name?: string
  is_typing?: boolean
}

function initializeSubscription() {
  if (globalSubscription) return

  const cable = getCableConsumer()
  globalSubscription = cable.subscriptions.create('ChatChannel', {
    received(data: ChatMessage) {
      // Call all registered handlers
      messageHandlers.forEach((handler) => handler(data))
    },
    connected() {
      console.log('ChatChannel: Connected')
    },
    disconnected() {
      console.log('ChatChannel: Disconnected')
      // Reset subscription so it can reconnect
      globalSubscription = null
    },
    rejected() {
      console.log('ChatChannel: Connection rejected')
      globalSubscription = null
    },
  })
}

// Chat subscription hook for real-time updates
export function useChatSubscription() {
  const queryClient = useQueryClient()
  const handlerRef = useRef<((data: ChatMessage) => void) | null>(null)

  useEffect(() => {
    // Initialize singleton subscription
    initializeSubscription()

    // Create handler for this component instance
    const handler = (data: ChatMessage) => {
      if (data.type === 'new_message' && data.message) {
        const msg = data.message
        // Directly update cache instead of invalidating (faster)
        queryClient.setQueryData(['conversation', msg.conversation_id], (old: ConversationCache | undefined) => {
          if (!old) return old

          // Determine if this message is from current user by checking existing messages
          // or if it matches our optimistic message
          const existingOwnMsg = old.messages?.find((m) => m.is_own)
          const currentUserId = existingOwnMsg?.user_id || 0

          // Check if message already exists (avoid duplicates from optimistic update)
          const tempMatch = old.messages?.find((m) =>
            m.id.toString().startsWith('temp-') && m.content === msg.content
          )

          if (tempMatch) {
            // Replace temp message with real one, keeping is_own from temp
            return {
              ...old,
              messages: old.messages.map((m) =>
                m.id === tempMatch.id ? { ...msg, is_own: true } : m
              ),
            }
          }

          // Check if message already exists by ID
          if (old.messages?.some((m) => m.id === msg.id)) {
            return old
          }

          // Add new message with is_own calculated
          const isOwn = currentUserId > 0 && msg.user_id === currentUserId

          // Play notification sound for messages from others
          if (!isOwn) {
            playNotificationSound()
          }

          return {
            ...old,
            messages: [...(old.messages || []), {
              ...msg,
              is_own: isOwn,
            }],
          }
        })
        // Update conversations list for last message preview
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      }

      if (data.type === 'typing') {
        queryClient.setQueryData(
          ['conversation', data.conversation_id, 'typing'],
          { user_id: data.user_id, user_name: data.user_name, is_typing: data.is_typing }
        )
      }
    }

    handlerRef.current = handler
    messageHandlers.add(handler)

    return () => {
      if (handlerRef.current) {
        messageHandlers.delete(handlerRef.current)
      }
    }
  }, [queryClient])

  // Send message via WebSocket
  const sendMessageWs = useCallback((conversationId: number, content: string) => {
    globalSubscription?.perform('send_message', {
      conversation_id: conversationId,
      content,
    })
  }, [])

  // Mark conversation as read via WebSocket
  const markReadWs = useCallback((conversationId: number) => {
    globalSubscription?.perform('mark_read', {
      conversation_id: conversationId,
    })
  }, [])

  // Send typing indicator
  const sendTyping = useCallback((conversationId: number, isTyping: boolean) => {
    globalSubscription?.perform('typing', {
      conversation_id: conversationId,
      is_typing: isTyping,
    })
  }, [])

  return { sendMessageWs, markReadWs, sendTyping }
}

// Separate hook for notifications - use only ONCE at app root level
export function useChatNotifications() {
  const { message: showMessageToast } = useToast()
  const showMessageRef = useRef(showMessageToast)
  const handlerRef = useRef<((data: ChatMessage) => void) | null>(null)

  // Keep ref updated without triggering effect
  useEffect(() => {
    showMessageRef.current = showMessageToast
  }, [showMessageToast])

  useEffect(() => {
    initializeSubscription()

    const handler = (data: ChatMessage) => {
      if (data.type === 'message_notification') {
        showMessageRef.current(data.sender_name || 'Nova mensagem', data.preview, {
          label: 'Ver',
          onClick: () => {
            window.location.href = `/app/social/chat/${encodeId(data.conversation_id!)}`
          },
        })
      }
    }

    handlerRef.current = handler
    messageHandlers.add(handler)

    return () => {
      if (handlerRef.current) {
        messageHandlers.delete(handlerRef.current)
      }
    }
  }, []) // Empty deps - only run once

  return null
}

// Get all conversations
export function useConversations() {
  // r7-25: sem a feature "conversas" no plano a API responde 403; o Sidebar chama isto
  // para o contador de nao lidas em toda tela.
  const enabled = usePlanFeature('conversas')
  return useQuery({
    queryKey: ['conversations'],
    queryFn: api.social.getConversations,
    enabled,
  })
}

// Get single conversation with messages
export function useConversation(id: number) {
  return useQuery({
    queryKey: ['conversation', id],
    queryFn: () => api.social.getConversation(id),
    enabled: !!id,
  })
}

// Create new conversation
export function useCreateConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { participantId?: number; participantIds?: number[]; name?: string; isGroup?: boolean }) =>
      api.social.createConversation(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// Send message via WebSocket with optimistic update
export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ conversationId, content }: { conversationId: number; content: string }) => {
      // Try WebSocket first (faster)
      if (globalSubscription) {
        globalSubscription.perform('send_message', {
          conversation_id: conversationId,
          content,
        })
        // Return optimistic response - real message will come via WS
        return { message: { id: Date.now(), content, is_own: true } }
      }
      // Fallback to HTTP if WS not available
      return api.social.sendMessage(conversationId, content)
    },
    onMutate: async ({ conversationId, content }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['conversation', conversationId] })

      // Snapshot previous value
      const previousData = queryClient.getQueryData(['conversation', conversationId])

      // Optimistically add message
      queryClient.setQueryData(['conversation', conversationId], (old: ConversationCache | undefined) => {
        if (!old) return old
        const optimisticMessage = {
          id: `temp-${Date.now()}`,
          content,
          user_id: 0, // Will be replaced by real message
          user_name: 'Você',
          avatar_initial: 'V',
          is_own: true,
          created_at: new Date().toISOString(),
          is_filtered: false,
        }
        return {
          ...old,
          messages: [...(old.messages || []), optimisticMessage],
        }
      })

      return { previousData }
    },
    onError: (_err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['conversation', variables.conversationId], context.previousData)
      }
    },
    onSettled: (_, __, variables) => {
      // Refetch after 500ms to get real message (WS should have it by then)
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['conversation', variables.conversationId] })
        queryClient.invalidateQueries({ queryKey: ['conversations'] })
      }, 500)
    },
  })
}

// Mark conversation as read
export function useMarkRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: number) => api.social.markRead(conversationId),
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// Toggle favorite status
export function useToggleFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: number) => api.social.toggleFavorite(conversationId),
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// Get typing status for a conversation (reactive)
interface TypingData {
  user_id: number
  user_name: string
  is_typing: boolean
}

export function useTypingStatus(conversationId: number) {
  const queryClient = useQueryClient()
  // Valor inicial semeado na propria criacao do estado, e nao dentro do efeito: o
  // `setState` sincrono ali pintava um quadro sem o "digitando..." que ja estava em
  // cache (react-hooks/set-state-in-effect). O efeito abaixo cuida so da assinatura.
  const readTyping = () =>
    queryClient.getQueryData<TypingData>(['conversation', conversationId, 'typing']) ?? null

  const [typingData, setTypingData] = useState<TypingData | null>(() => {
    const initial = readTyping()
    return initial?.is_typing ? initial : null
  })

  // Trocar de conversa precisa reler o cache — o inicializador acima so roda na montagem.
  const [conversationSnapshot, setConversationSnapshot] = useState(conversationId)
  if (conversationId !== conversationSnapshot) {
    setConversationSnapshot(conversationId)
    const initial = readTyping()
    setTypingData(initial?.is_typing ? initial : null)
  }

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Subscribe to changes
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.query.queryKey[0] === 'conversation' &&
        event.query.queryKey[1] === conversationId &&
        event.query.queryKey[2] === 'typing'
      ) {
        const data = event.query.state.data as TypingData | undefined

        if (data?.is_typing) {
          setTypingData(data)

          // Auto-clear after 3 seconds if no update
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
          timeoutRef.current = setTimeout(() => {
            setTypingData(null)
          }, 3000)
        } else {
          setTypingData(null)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
          }
        }
      }
    })

    return () => {
      unsubscribe()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [queryClient, conversationId])

  return typingData
}
